// hooks/useProfileQr.ts — 1.24.00 E3.
//
// Fetches the profile QR the moment the caller mounts, which is why
// ShareProfileDialog is mount-on-open: nobody pays for a QR they never see.
//
// Failure is SILENT to the member: a QR is a convenience beside a link that is
// already on screen and already copyable, so a red error where a square should
// be is worse than nothing. The dialog just renders no QR section.
//
// Components must NOT import profileQrService directly.

import { useEffect, useState } from 'react';
import { profileQrService } from '@/core/services/profileQrService';

export interface ProfileQrHook {
  /** The raw `<svg …>` string, or null while loading / on failure. */
  qrSvg: string | null;
  loading: boolean;
}

export function useProfileQr(handle: string): ProfileQrHook {
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flag
    setLoading(true);
    profileQrService
      .forHandle(handle)
      .then((r) => {
        // A server that answered without a string is the same as no QR — never
        // let `undefined` reach encodeURIComponent and render "undefined".
        if (alive) setQrSvg(typeof r?.qrSvg === 'string' && r.qrSvg ? r.qrSvg : null);
      })
      .catch(() => {
        if (alive) setQrSvg(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [handle]);

  return { qrSvg, loading };
}
